import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ArrowRight, Award, Building2, CheckCircle } from "lucide-react";
import { DEVELOPERS } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "/sneha-logo.jpg";

const Developers = () => {
  const [developers, setDevelopers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/developers`)
      .then((r) => { setDevelopers(r.data); setSelected(r.data[0]); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-[#080808] min-h-screen pt-24">
      {/* Header */}
      <div className="py-16 border-b border-white/5"
        style={{ background: "linear-gradient(180deg, #0a0900 0%, #080808 100%)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="section-label">
            Developer Partners
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="font-display text-4xl md:text-5xl font-light text-[#FAF8F3]">
            India's Most Trusted<br />
            <span className="text-gold-gradient italic">Real Estate Groups</span>
          </motion.h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {loading ? (
          <div className="flex justify-center py-24">
            <div className="w-8 h-8 border border-[#D4AF37]/20 border-t-[#D4AF37] rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Developer list */}
            <div className="space-y-3" data-testid={DEVELOPERS.grid}>
              {developers.map((dev, i) => (
                <motion.button key={dev.id}
                  initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.07 }}
                  onClick={() => setSelected(dev)}
                  data-testid={DEVELOPERS.card}
                  className={`w-full text-left p-5 border transition-all duration-300 ${
                    selected?.id === dev.id
                      ? "border-[#D4AF37]/40 bg-[#D4AF37]/5"
                      : "border-white/5 hover:border-white/10 bg-[#0a0a0a]"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center text-sm font-heading font-semibold"
                      style={{ background: `${dev.logo_color}20`, color: dev.logo_color, border: `1px solid ${dev.logo_color}30` }}>
                      {dev.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm text-[#FAF8F3] font-light">{dev.name}</p>
                      <p className="text-[10px] text-[#666]">Est. {dev.established}</p>
                    </div>
                    <div className="ml-auto text-right">
                      <p className="text-sm text-[#D4AF37] font-display">{dev.avg_appreciation}</p>
                      <p className="text-[9px] text-[#444]">Avg ROI</p>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>

            {/* Developer detail */}
            <div className="lg:col-span-2">
              <AnimatePresence mode="wait">
                {selected && (
                  <motion.div key={selected.id}
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
                    className="glass-gold p-8"
                  >
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-14 h-14 flex items-center justify-center text-xl font-heading font-bold"
                        style={{ background: `${selected.logo_color}20`, color: selected.logo_color, border: `2px solid ${selected.logo_color}40` }}>
                        {selected.name.charAt(0)}
                      </div>
                      <div>
                        <h2 className="font-display text-2xl text-[#FAF8F3] font-light">{selected.name}</h2>
                        <p className="text-xs text-[#D4AF37] tracking-wider">{selected.tagline}</p>
                      </div>
                    </div>

                    <p className="text-[#A0A0A0] text-sm leading-relaxed mb-8">{selected.description}</p>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                      {[
                        { label: "Established", value: selected.established },
                        { label: "Projects", value: `${selected.projects_completed}+` },
                        { label: "Units Delivered", value: `${(selected.units_delivered / 1000).toFixed(0)}k+` },
                        { label: "Delivery Record", value: selected.delivery_record },
                      ].map((m) => (
                        <div key={m.label} className="border border-white/5 p-4 text-center">
                          <p className="font-display text-xl text-[#D4AF37]">{m.value}</p>
                          <p className="text-[#444] text-[10px] tracking-wider mt-1">{m.label}</p>
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <p className="text-xs text-[#D4AF37] tracking-wider uppercase mb-3">Focus Areas</p>
                        <div className="flex flex-wrap gap-2">
                          {(selected.focus_areas || []).map((area) => (
                            <span key={area} className="text-xs border border-white/10 px-3 py-1 text-[#A0A0A0]">{area}</span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-[#D4AF37] tracking-wider uppercase mb-3">Notable Projects</p>
                        {(selected.notable_projects || []).map((proj) => (
                          <div key={proj} className="flex items-center gap-2 mb-2">
                            <CheckCircle size={11} className="text-[#D4AF37] shrink-0" />
                            <span className="text-xs text-[#A0A0A0]">{proj}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="mt-8 pt-6 border-t border-white/5 flex flex-wrap gap-4">
                      <Link to="/properties" className="btn-gold text-xs py-3 px-6">
                        View Properties <ArrowRight size={12} className="inline ml-2" />
                      </Link>
                      <Link to="/consultation" className="btn-gold-solid btn-gold text-xs py-3 px-6">
                        Book Consultation
                      </Link>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        )}

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="mt-16 text-center glass-gold p-10"
        >
          <img src={LOGO} alt="Sneha Reality" className="h-14 w-auto object-contain mx-auto mb-4" />
          <h3 className="font-display text-2xl text-[#FAF8F3] mb-3">Partner with Sneha Reality</h3>
          <p className="text-[#666] text-sm mb-6 max-w-md mx-auto">
            We work exclusively with India's most reputed developers to curate investment opportunities with verified track records.
          </p>
          <Link to="/consultation" className="btn-gold-solid btn-gold text-xs py-3 px-8">
            Get Investment Advisory <ArrowRight size={12} className="inline ml-2" />
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default Developers;
